import React, { useState, useMemo, useContext } from 'react';
import Modal from '../../common/Modal';
import Icon from '../../common/Icon';
import {
  beginExport,
  createExport,
  destroyExport,
  generateParams,
} from './constants/handleExports';
import ExportFieldSortable from './ExportFieldSortable';
import { QueryContext } from '../../../hooks/Contexts';
import { confirmation } from '../../../helpers/notifications';

const SubmissionExportModal = ({
  viewSubExport,
  setViewSubExport,
  formExports,
  setFormExports,
  fieldsets,
  formID,
  allowParentOption,
}) => {
  const { queryParams } = useContext(QueryContext);
  const [selectedFields, setSelectedFields] = useState([]);
  const [selectedExport, setSelectedExport] = useState(null);
  const [includeParent, setIncludeParent] = useState(false);
  const fields = useMemo(() => fieldsets.map((fs) => fs.fields).flat(), []);
  const fieldsFilter = (field) =>
    selectedFields.length
      ? !selectedFields.map((f) => f.id).includes(field.id)
      : true;

  const handleExport = () => {
    let params = generateParams(
      formID,
      queryParams.filters,
      includeParent,
      selectedFields.map((f) => f.id)
    );
    beginExport(params);
    handleModalClose();
  };

  const handleExportAll = () => {
    let params = generateParams(formID, queryParams.filters, includeParent);
    beginExport(params);
    handleModalClose();
  };

  const handleSave = async () => {
    if (selectedFields.length > 0) {
      confirmation.prompt(
        'Create Export',
        'Enter export name:',
        '',
        async (name) => {
          if (!name) return;
          let fields = selectedFields.map((f) => f.id);
          let newExport = await createExport(formID, name, fields);
          if (newExport.ok) {
            setFormExports([...formExports, newExport.data]);
            setSelectedExport(newExport.data);
          }
        }
      );
    }
  };

  const handleDestroy = async () => {
    if (selectedExport) {
      await destroyExport(selectedExport.id, (result) => {
        if (result.ok) {
          setSelectedExport(null);
          setFormExports([
            ...formExports.filter((x) => x.id != selectedExport.id),
          ]);
          setSelectedFields([]);
        }
      });
    }
  };

  const handleSelectExport = (e) => {
    let savedExport = formExports.find((x) => x.id == e.target.value);
    if (savedExport) {
      let exportFields = savedExport.export_fields.map((x) =>
        fields.find((f) => f.id == x.field_id)
      );
      setSelectedFields(exportFields);
    }
    setSelectedExport(savedExport || null);
  };

  const addField = (e) => {
    let fieldId = e.target.value;
    setSelectedFields([...selectedFields, fields.find((f) => f.id == fieldId)]);
    clearSavedExport();
  };

  const removeField = (fieldId) => {
    setSelectedFields(selectedFields.filter((f) => f.id != fieldId));
    clearSavedExport();
  };

  const clearSavedExport = () => {
    setSelectedExport(null);
    $('#export-select').val('');
  };

  const handleModalClose = () => {
    clearSavedExport();
    setSelectedFields([]);
    setViewSubExport(false);
    setIncludeParent(false);
  };

  return (
    <Modal
      show={viewSubExport}
      onClose={handleModalClose}
      title='submission-exporter'
    >
      <header>
        <h3>Export Submissions Data</h3>
      </header>
      <div className='submission-exporter'>
        <div className='builder'>
          <div className='creation-form'>
            <div className='row'>
              <div className='full'>
                <div className='select-input'>
                  <select
                    name='count-field-select'
                    id='count-field-select'
                    onChange={addField}
                  >
                    <option label='Choose field...' value='' />
                    {fieldsets.map((fs) => {
                      return (
                        <optgroup key={fs.id} label={fs.name}>
                          {fs.fields.filter(fieldsFilter).map((field) => {
                            return (
                              <option key={field.id + '-add'} value={field.id}>
                                {field.name}
                              </option>
                            );
                          })}
                        </optgroup>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {selectedFields.length > 0 && (
            <ExportFieldSortable
              selectedFields={selectedFields}
              setSelectedFields={setSelectedFields}
              removeField={removeField}
              clearSavedExport={clearSavedExport}
            />
          )}

          {allowParentOption && (
            <div className='row parent-sub'>
              <input
                type='checkbox'
                id='include-parent-sub'
                checked={includeParent}
                onChange={() => setIncludeParent(!includeParent)}
              />
              <label htmlFor='include-parent-sub'></label>
              <div className='name'> Include Parent Submission ID</div>
            </div>
          )}
        </div>

        <div className='controls'>
          <div className='left creation-form'>
            <div className='select-input'>
              {formExports && (
                <select
                  onChange={handleSelectExport}
                  id='export-select'
                  value={selectedExport?.id}
                >
                  <option value='' label='Saved Exports...' />
                  {formExports.map((exp) => {
                    return (
                      <option key={exp.id} value={exp.id} label={exp.name} />
                    );
                  })}
                </select>
              )}
            </div>
            {selectedFields.length > 0 && (
              <Icon
                title='Save this export'
                icon='save'
                onClick={handleSave}
                className='export-icon'
                isDiv
              />
            )}
            {selectedExport && (
              <Icon
                title='Remove this export'
                icon='trash fa-sm'
                onClick={handleDestroy}
                className='export-icon'
                isDiv
              />
            )}
          </div>
          <div className='right'>
            <button
              id='export-btn'
              onClick={handleExport}
              disabled={!selectedFields.length}
            >
              Export
            </button>

            <button id='export-all-btn' onClick={handleExportAll}>
              Export All Fields
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SubmissionExportModal;
